import { useEffect, useState } from 'react';
import { ActionIcon, Card, CopyButton, Divider, Grid, Group, Loader, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import type { ExpeditorProfileDto } from '@expeditor/shared';
import { expeditorProfileApi } from '../../shared/api/expeditor-profile';

/** Пара «подпись — значение»; пустые значения показываем прочерком. */
function Field({
  label,
  value,
  copyable,
}: {
  label: string;
  value?: string | null;
  copyable?: boolean;
}) {
  const text = value?.trim() ?? '';
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {copyable && text ? (
        <Group gap={6} wrap="nowrap">
          <Text size="sm">{text}</Text>
          <CopyButton value={text}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Скопировано' : 'Скопировать'} withArrow>
                <ActionIcon size="sm" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
      ) : (
        <Text size="sm">{text || '—'}</Text>
      )}
    </Stack>
  );
}

/**
 * Реквизиты экспедитора «как есть» — только для чтения.
 *
 * Клиенту эти сведения нужны в профиле (раньше жили на отдельной странице
 * «Об экспедиторе»); правит их администратор в своём разделе.
 */
export function ExpeditorInfoCard() {
  const [dto, setDto] = useState<ExpeditorProfileDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    expeditorProfileApi
      .get()
      .then((d) => !cancelled && setDto(d))
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card withBorder>
      <Title order={4} mb="sm">
        Экспедитор
      </Title>
      {loading ? (
        <Loader size="sm" />
      ) : !dto ? (
        <Text size="sm" c="dimmed">
          Реквизиты экспедитора пока не заполнены.
        </Text>
      ) : (
        <Stack gap="sm">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <Field label="Полное наименование" value={dto.fullName} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Field label="Краткое наименование" value={dto.shortName} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Field label="ИНН" value={dto.inn} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Field label="ОГРН" value={dto.ogrn} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Field label="КПП" value={dto.kpp} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Юридический адрес" value={dto.legalAddress} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Почтовый адрес" value={dto.postalAddress} />
            </Grid.Col>
          </Grid>

          <Divider />
          <Text fw={500} size="sm">
            Контакты и руководитель
          </Text>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Контактный email" value={dto.contactEmail} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Телефон" value={dto.contactPhone} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Руководитель" value={dto.directorName} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Должность" value={dto.directorPosition} />
            </Grid.Col>
          </Grid>

          <Divider />
          <Text fw={500} size="sm">
            Банковские реквизиты
          </Text>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Банк" value={dto.bankName} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="БИК" value={dto.bankBik} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Корреспондентский счёт" value={dto.bankCorrAccount} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Field label="Расчётный счёт" value={dto.bankAccount} />
            </Grid.Col>
          </Grid>

          {dto.edoSubscriberId?.trim() && (
            <>
              <Divider />
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Field label="Код абонента ЭДО" value={dto.edoSubscriberId} copyable />
                </Grid.Col>
              </Grid>
            </>
          )}
        </Stack>
      )}
    </Card>
  );
}
